/**
 * Comprovante enviado pelo cliente (spec 039) e o PIX da marca (spec 047): a mensalidade e o pedido de avanco sao pagos
 * pela Pagar.me (PIX ou cartao, baixa na hora) ou, se o cliente escolher, pelo PIX direto da marca, e o comprovante
 * enviado na mesma tela e o que um administrador confere pra dar a baixa. As mesmas
 * pecas servem a area (a mensalidade, o pedido e o Perfil) e o painel (a fila, a ficha e a conferencia). Nenhuma sabe a
 * marca nem decide nada: estado, rotulo, valor, chave, prazo e motivo vem do backend.
 */
import { useEffect, useRef, type FormEvent, type ReactNode } from "react";
import { formatarBytes, formatarDataHora, formatarDia, formatarReais, ROTULO_DA_CHAVE_PIX, type TipoDeChavePix } from "../formatos";
import { IconeArquivo, IconeEnviar, IconeExterno, IconeMensagem } from "../icones";
import { Aviso, Carregando, Selo, type Tom } from "./Basicos";
import { Botao } from "./Botao";
import { ArquivoRecusado, CampoArquivo, CampoData, CampoTextoLongo, type FalhaDoArquivo } from "./Campo";
import { Fatos, TextoCopiavel, type Fato } from "./Pagina";

export type EstadoDoComprovante = "EM_ANALISE" | "CONFERIDO" | "RECUSADO";

/** O que as rotas de comprovante devolvem, sem o arquivo (que sai pela rota propria). `pessoa` e `decidido_por`, so no painel. */
export type Comprovante = {
  id: string;
  matricula_id: string;
  alvo: { tipo: "MENSALIDADE" | "PEDIDO"; id: string };
  descricao: string;
  valor_centavos: number | null;
  pago_no_dia: string;
  observacao: string | null;
  arquivo: { nome: string; tipo: string; tamanho: number };
  estado: EstadoDoComprovante;
  estado_rotulo: string;
  motivo: string | null;
  decidido_em: string | null;
  enviado_em: string;
  pessoa?: { id: string; nome: string; cpf: string } | null;
  decidido_por?: string | null;
};

export const TOM_DO_COMPROVANTE: Record<EstadoDoComprovante, Tom> = { EM_ANALISE: "info", CONFERIDO: "sucesso", RECUSADO: "erro" };

/** Os tipos que a equipe consegue abrir na tela (o backend confere pelo conteudo; aqui e so pra avisar antes de enviar). */
export const TIPOS_DE_COMPROVANTE = "application/pdf,image/png,image/jpeg";
export const TAMANHO_DO_COMPROVANTE = 10 * 1024 * 1024;
/**
 * O seletor mostra toda foto e PDF: com so PNG e JPG, o Android esconde a foto em HEIC ou WEBP e a pessoa acha que o
 * comprovante sumiu. O `prepararComprovante` converte o que nao for PNG nem JPG.
 */
export const ACEITA_NO_SELETOR = "image/*,application/pdf";
/** Foto JPG ou PNG ate aqui vai como esta; maior, ou em outro formato, vira JPG com o lado maior ate LADO_MAXIMO. */
const FOTO_SEM_MEXER_ATE = 2 * 1024 * 1024;
const LADO_MAXIMO = 2400;

const ehPdf = (f: File) => f.type === "application/pdf" || (!f.type && /\.pdf$/i.test(f.name));
const ehImagem = (f: File) => f.type.startsWith("image/") || (!f.type && /\.(jpe?g|png|heic|heif|webp|gif|bmp|avif)$/i.test(f.name));

async function decodificar(f: File): Promise<{ largura: number; altura: number; desenhar: (c: CanvasRenderingContext2D, l: number, a: number) => void; soltar: () => void }> {
  if (typeof createImageBitmap === "function") {
    try {
      const b = await createImageBitmap(f, { imageOrientation: "from-image" });
      return { largura: b.width, altura: b.height, desenhar: (c, l, a) => c.drawImage(b, 0, 0, l, a), soltar: () => b.close() };
    } catch {
      // Alguns navegadores abrem pela <img> o que o createImageBitmap recusa (o Safari com HEIC, por exemplo).
    }
  }
  const url = URL.createObjectURL(f);
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
  return { largura: img.naturalWidth, altura: img.naturalHeight, desenhar: (c, l, a) => c.drawImage(img, 0, 0, l, a), soltar: () => URL.revokeObjectURL(url) };
}

/**
 * Deixa o comprovante pronto pra enviar (28/10, "trabalhe nos casos de envio pra que nao volte a dar erro"): PDF vai
 * como esta; foto em HEIC, WEBP e afins vira JPG; foto grande (a camera passa de 10 MB) encolhe pra JPG com o lado
 * maior ate 2400 px, que ainda le o comprovante inteiro. Sem conseguir abrir a foto, pede o print, que todo aparelho
 * tira em PNG ou JPG.
 */
export async function prepararComprovante(f: File): Promise<File> {
  if (ehPdf(f) || !ehImagem(f)) return f;
  const aceita = f.type === "image/jpeg" || f.type === "image/png";
  if (aceita && f.size <= FOTO_SEM_MEXER_ATE) return f;
  let imagem: Awaited<ReturnType<typeof decodificar>>;
  try {
    imagem = await decodificar(f);
  } catch {
    if (aceita && f.size <= TAMANHO_DO_COMPROVANTE) return f;
    throw new ArquivoRecusado("Seu aparelho não deixou abrir essa foto (pode ser o formato HEIC). Tire um print da tela do comprovante e envie o print.");
  }
  try {
    const escala = Math.min(1, LADO_MAXIMO / Math.max(imagem.largura, imagem.altura));
    const l = Math.max(1, Math.round(imagem.largura * escala));
    const a = Math.max(1, Math.round(imagem.altura * escala));
    const canvas = document.createElement("canvas");
    canvas.width = l;
    canvas.height = a;
    const c = canvas.getContext("2d");
    if (!c) throw new Error("canvas sem contexto 2d");
    // Fundo branco: PNG transparente vira JPG preto sem ele.
    c.fillStyle = "#fff";
    c.fillRect(0, 0, l, a);
    imagem.desenhar(c, l, a);
    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/jpeg", 0.85));
    if (!blob || !blob.size) throw new Error("canvas nao gerou o JPG");
    return new File([blob], `${f.name.replace(/\.[^.]*$/, "") || "comprovante"}.jpg`, { type: "image/jpeg", lastModified: f.lastModified });
  } catch (e) {
    if (aceita && f.size <= TAMANHO_DO_COMPROVANTE) return f;
    throw e;
  } finally {
    imagem.soltar();
  }
}

const formato = (tipo: string) => (tipo.startsWith("image/") ? "Imagem" : "PDF");

/**
 * Um comprovante numa lista: o que ele paga, o valor, o dia do pagamento, o estado e, recusado, o motivo que o cliente le.
 * `acoes`: os botoes do lugar (baixar, conferir). `children`: o que abre por baixo dele (a conferencia, no painel).
 */
export function CartaoDeComprovante({ c, acoes, destaque, mostrarPessoa, rotuloDaObservacao = "Observação do cliente", children }: { c: Comprovante; acoes?: ReactNode; destaque?: boolean; mostrarPessoa?: boolean; /** Na area, "Sua observação". */ rotuloDaObservacao?: string; children?: ReactNode }) {
  return (
    <article className="mtc-comprovante" data-destaque={destaque || undefined} aria-label={`${c.descricao}, ${c.estado_rotulo.toLowerCase()}`}>
      <div className="mtc-comprovante__cabeca">
        <div className="mtc-comprovante__textos">
          {mostrarPessoa && c.pessoa && <span className="mtc-rotulo">{c.pessoa.nome}</span>}
          <strong className="mtc-comprovante__titulo">{c.descricao}</strong>
          <span className="mtc-comprovante__meta">
            {[c.valor_centavos != null ? formatarReais(c.valor_centavos) : null, `pago em ${formatarDia(c.pago_no_dia)}`, `enviado em ${formatarDataHora(c.enviado_em)}`].filter(Boolean).join(" · ")}
          </span>
        </div>
        <Selo tom={TOM_DO_COMPROVANTE[c.estado]}>{c.estado_rotulo}</Selo>
      </div>
      {c.estado === "RECUSADO" && c.motivo && <Aviso tipo="alerta"><strong>Motivo da recusa:</strong> {c.motivo}</Aviso>}
      {c.observacao && <p className="mtc-comprovante__obs">{rotuloDaObservacao}: {c.observacao}</p>}
      <div className="mtc-comprovante__rodape">
        <span className="mtc-comprovante__arquivo"><IconeArquivo tamanho={16} /><span>{formato(c.arquivo.tipo)}, {formatarBytes(c.arquivo.tamanho)}</span></span>
        {acoes && <div className="mtc-comprovante__acoes">{acoes}</div>}
      </div>
      {children}
    </article>
  );
}

/** Lista de comprovantes (Perfil, ficha da pessoa). */
export function ListaDeComprovantes({ children, rotulo }: { children: ReactNode; rotulo: string }) {
  return <div className="mtc-lista-comprovantes" role="list" aria-label={rotulo}>{children}</div>;
}

/** A promessa ao cliente quando o app nao tem a frase do backend em maos (a tela foi recarregada depois do envio). */
const PRAZO_DA_LIBERACAO = "A liberação leva até 1 dia depois do pagamento: um administrador confere o comprovante e libera.";

/**
 * O que aconteceu com o ultimo comprovante de uma cobranca (a mensalidade em aberto, o pedido que espera pagamento) e,
 * por baixo, as `acoes` do lugar (o "Pagar", o "Pagar pelo PIX direto" e, recusado, o "Enviar outro comprovante") com a
 * `nota` que explica a diferenca entre os dois caminhos. Em analise: a confirmacao com o
 * prazo, e o app nao oferece pagar de novo (o backend recusa outro enquanto este esta em analise). Recusado: o motivo.
 * Sem comprovante (ou ja conferido), so as acoes: o envio faz parte do pagar (spec 047). `prazo`: a frase do backend,
 * quando o app tem.
 */
export function ComprovanteDaCobranca({ ultimo, prazo, acoes, nota }: { ultimo?: Pick<Comprovante, "estado" | "enviado_em" | "motivo"> | null; prazo?: string; acoes?: ReactNode; nota?: ReactNode }) {
  const estado = ultimo?.estado === "EM_ANALISE"
    ? <Aviso tipo="info"><strong>Comprovante em análise</strong>, enviado em {formatarDataHora(ultimo.enviado_em)}. {prazo || PRAZO_DA_LIBERACAO} Você recebe o aviso aqui.</Aviso>
    : ultimo?.estado === "RECUSADO"
      ? <Aviso tipo="alerta"><strong>Comprovante não aceito.</strong> {ultimo.motivo}</Aviso>
      : null;
  if (!estado && !acoes) return null;
  return (
    <div className="mtc-cobranca">
      {estado}
      {acoes && <div className="mtc-cobranca__acoes">{acoes}</div>}
      {acoes && nota && <p className="mtc-cobranca__nota">{nota}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- PIX da marca (spec 047)

/** O que `GET /v1/me/pix` devolve: o que paga, o valor exato, quem recebe, o QR (PNG em data URL) e a frase do prazo. */
export type PixParaPagar = {
  descricao: string;
  valor_centavos: number;
  favorecido: string;
  tipo_da_chave: TipoDeChavePix;
  /** Ja formatada pelo backend (CPF com pontos, telefone com DDD). */
  chave: string;
  copia_e_cola: string;
  qr_code: string;
  prazo: string;
};

/** O envio do comprovante dentro do PIX: os campos, o erro do backend e o enviar. O app faz a chamada. */
/** `aoFalhar`: a leitura ou o preparo do arquivo falhou no aparelho (o app registra; a tela ja avisou a pessoa). */
export type EnvioDoComprovante = { valor: DadosDoEnvio; aoMudar: (v: DadosDoEnvio) => void; hoje: string; enviando?: boolean; erro?: string; aoEnviar: () => void; aoFalhar?: (falha: FalhaDoArquivo) => void };

/** Pronto pra enviar: arquivo aceito e o dia do pagamento ate hoje (o backend confere de novo). */
export const envioPronto = (v: DadosDoEnvio, hoje: string) => Boolean(v.arquivo) && !problemaDoComprovante(v.arquivo) && Boolean(v.dia) && v.dia <= hoje;

/**
 * O pagamento pelo PIX da marca, com o comprovante na mesma tela (spec 047): (1) o valor exato, o copia e cola, o QR, o
 * favorecido e a chave; (2) o envio do comprovante, com o prazo da liberacao. A baixa nunca e automatica: quem libera e
 * um administrador, pelo comprovante. Cabe dentro do cartao da cobranca (sem cartao proprio, so a linha de cima).
 * `carregando`: buscando o PIX. `erro`: a frase do backend quando nao da pra pagar agora (sem chave, valor a definir);
 * com `suporte`, o link pra falar com o suporte. `focarEnvio`: abre com o foco no envio (o "Enviar outro comprovante").
 */
export function PixDaMarca({ pix, carregando, erro, suporte, envio, aoFechar, focarEnvio }: { pix?: PixParaPagar | null; carregando?: boolean; erro?: string; suporte?: string | null; envio: EnvioDoComprovante; aoFechar: () => void; focarEnvio?: boolean }) {
  const passo2 = useRef<HTMLLIElement>(null);
  const pronto = Boolean(pix) && !carregando && !erro;
  useEffect(() => {
    if (pronto && focarEnvio) passo2.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [pronto, focarEnvio]);
  const enviar = (ev: FormEvent) => {
    ev.preventDefault();
    if (envioPronto(envio.valor, envio.hoje)) envio.aoEnviar();
  };
  if (carregando) return <section className="mtc-pix-marca" aria-label="Pagar pelo PIX direto" aria-busy><Carregando compacto texto="Gerando o PIX" /></section>;
  if (erro || !pix) {
    return (
      <section className="mtc-pix-marca" aria-label="Pagar pelo PIX direto">
        <Aviso tipo="alerta">{erro ?? "O PIX não abriu agora. Tente de novo em instantes."}</Aviso>
        <div className="mtc-pix-marca__rodape">
          <Botao variante="fantasma" onClick={aoFechar}>Fechar</Botao>
          {suporte && <a className="mtc-botao mtc-botao--secundario" href={suporte} target="_blank" rel="noreferrer"><IconeMensagem tamanho={18} />Falar com o suporte</a>}
        </div>
      </section>
    );
  }
  return (
    <section className="mtc-pix-marca" aria-label="Pagar pelo PIX direto">
      <ol className="mtc-pix-marca__passos">
        <li className="mtc-pix-marca__passo">
          <h3 className="mtc-pix-marca__titulo"><span className="mtc-pix-marca__numero" aria-hidden>1</span>Pague pelo PIX direto</h3>
          <div className="mtc-pix-marca__pagar">
            <div className="mtc-pix-marca__valor">
              <span className="mtc-rotulo">Valor exato</span>
              <strong className="mtc-pix-marca__preco mtc-mono" data-valor>{formatarReais(pix.valor_centavos)}</strong>
              <span className="mtc-pix-marca__descricao">{pix.descricao}</span>
            </div>
            <div className="mtc-pix-marca__codigo">
              <span className="mtc-rotulo">PIX copia e cola</span>
              <TextoCopiavel valor={pix.copia_e_cola} rotulo="Copiar código" />
            </div>
            <div className="mtc-pix__qr mtc-pix-marca__qr"><img src={pix.qr_code} alt="QR Code do PIX" width={200} height={200} /></div>
            <div className="mtc-pix-marca__quem">
              <Fatos itens={[{ rotulo: "Favorecido", valor: pix.favorecido }, { rotulo: `Chave PIX (${ROTULO_DA_CHAVE_PIX[pix.tipo_da_chave] ?? pix.tipo_da_chave})`, valor: pix.chave, mono: true }]} />
            </div>
            <p className="mtc-pix-marca__dica">No app do seu banco, escolha PIX copia e cola (ou leia o QR Code com outro aparelho). Confira o valor e o favorecido antes de confirmar.</p>
          </div>
        </li>
        <li className="mtc-pix-marca__passo" ref={passo2}>
          <h3 className="mtc-pix-marca__titulo"><span className="mtc-pix-marca__numero" aria-hidden>2</span>Envie o comprovante</h3>
          <form className="mtc-formulario" onSubmit={enviar} noValidate>
            <CamposDoComprovante valor={envio.valor} aoMudar={envio.aoMudar} hoje={envio.hoje} aoFalhar={envio.aoFalhar} />
            <Aviso tipo="info">{pix.prazo}</Aviso>
            {/* O erro do envio junto do botao: no celular, la em cima ele ficava fora da tela de quem tocou em enviar. */}
            {envio.erro && <Aviso tipo="erro">{envio.erro}</Aviso>}
            <div className="mtc-pix-marca__rodape">
              <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
              {/* Deu erro no envio: o WhatsApp do suporte ao lado do Enviar, pra pessoa nunca ficar sem saida. */}
              {envio.erro && suporte && <a className="mtc-botao mtc-botao--secundario" href={suporte} target="_blank" rel="noreferrer"><IconeMensagem tamanho={18} />Falar com o suporte</a>}
              <Botao type="submit" icone={<IconeEnviar tamanho={18} />} carregando={envio.enviando} disabled={!envioPronto(envio.valor, envio.hoje)}>Enviar comprovante</Botao>
            </div>
          </form>
        </li>
      </ol>
    </section>
  );
}

export type DadosDoEnvio = { arquivo: File | null; dia: string; observacao: string };

/** O que ha de errado com o arquivo antes de enviar (tipo e tamanho); o backend confere de novo pelo conteudo. */
export function problemaDoComprovante(f: File | null): string | undefined {
  if (!f) return undefined;
  if (f.size > TAMANHO_DO_COMPROVANTE) return "O arquivo passou de 10 MB. Envie uma foto menor ou o PDF.";
  if (f.type && !TIPOS_DE_COMPROVANTE.split(",").includes(f.type)) return "Envie o comprovante em PDF ou foto (PNG ou JPG). Um print da tela também serve.";
  return undefined;
}

/**
 * Os campos do envio: o arquivo, o dia em que pagou (hoje de padrao, nunca no futuro) e uma observacao opcional. O app
 * poe dentro do dialogo dele, com os botoes. `hoje`: o dia de Sao Paulo, que vira o limite do seletor.
 */
export function CamposDoComprovante({ valor, aoMudar, hoje, aoFalhar }: { valor: DadosDoEnvio; aoMudar: (v: DadosDoEnvio) => void; hoje: string; aoFalhar?: (falha: FalhaDoArquivo) => void }) {
  return (
    <>
      <CampoArquivo rotulo="Arquivo do comprovante" arquivo={valor.arquivo} aoEscolher={(arquivo) => aoMudar({ ...valor, arquivo })} aceita={ACEITA_NO_SELETOR} preparar={prepararComprovante} aoFalhar={aoFalhar} erro={problemaDoComprovante(valor.arquivo)} dica="PDF ou foto, até 10 MB. Pode ser a foto ou o print do comprovante." />
      <CampoData rotulo="Dia do pagamento" valor={valor.dia} aoMudar={(dia) => aoMudar({ ...valor, dia })} max={hoje} required erro={valor.dia > hoje ? "O dia do pagamento não pode ser no futuro." : undefined} dica="O dia em que o dinheiro saiu da sua conta." />
      <CampoTextoLongo rotulo="Observação (opcional)" valor={valor.observacao} aoMudar={(observacao) => aoMudar({ ...valor, observacao })} maxLength={500} linhas={2} placeholder="Ex.: paguei pelo PIX da conta da minha empresa." />
    </>
  );
}

/**
 * O arquivo na tela: imagem inteira, PDF no leitor do navegador. `url` e o endereco local do arquivo baixado pela API
 * autenticada (nunca link publico). Sempre com o "Abrir em outra aba", pra ampliar ou quando o navegador nao mostra PDF.
 */
export function VisorDeArquivo({ url, tipo, nome, carregando, erro }: { url: string | null; tipo: string; nome: string; carregando?: boolean; erro?: string }) {
  const imagem = tipo.startsWith("image/");
  return (
    <figure className="mtc-visor">
      <div className="mtc-visor__quadro" data-pdf={!imagem || undefined}>
        {carregando ? (
          <Carregando compacto texto="Abrindo o arquivo" />
        ) : erro ? (
          <div className="mtc-visor__aviso"><Aviso tipo="erro">{erro}</Aviso></div>
        ) : url && imagem ? (
          <img className="mtc-visor__imagem" src={url} alt={`Comprovante enviado: ${nome}`} />
        ) : url ? (
          <object className="mtc-visor__pdf" data={url} type="application/pdf" aria-label={`Comprovante enviado: ${nome}`}>
            <p className="mtc-visor__aviso mtc-texto-2">Este navegador não mostra o PDF aqui dentro. Use o “Abrir em outra aba”.</p>
          </object>
        ) : null}
      </div>
      <figcaption className="mtc-visor__legenda">
        <span className="mtc-mono">{nome}</span>
        {url && <a className="mtc-link mtc-visor__abrir" href={url} target="_blank" rel="noreferrer">Abrir em outra aba <IconeExterno tamanho={14} /></a>}
      </figcaption>
    </figure>
  );
}

/**
 * A conferencia (painel): o arquivo ao lado do que ele paga, pra comparar na mesma tela. `visor`: o `VisorDeArquivo`;
 * `acoes`: conferir e recusar (ou a decisao ja tomada); `extras`: fatos a mais (a mensalidade, o pedido) que o app tenha.
 */
export function ConferenciaDeComprovante({ c, visor, acoes, extras, links }: { c: Comprovante; visor: ReactNode; acoes?: ReactNode; extras?: Fato[]; links?: ReactNode }) {
  const decisao = c.estado !== "EM_ANALISE" && c.decidido_em ? `${c.estado === "CONFERIDO" ? "Conferido" : "Recusado"} por ${c.decidido_por ?? "administrador"} em ${formatarDataHora(c.decidido_em)}` : null;
  return (
    <div className="mtc-conferencia">
      <div className="mtc-conferencia__grade">
        <div className="mtc-conferencia__arquivo">{visor}</div>
        <div className="mtc-conferencia__dados">
          <Fatos
            itens={[
              ...(c.pessoa ? [{ rotulo: "Cliente", valor: c.pessoa.nome, detalhe: <span className="mtc-mono">{c.pessoa.cpf}</span> }] : []),
              { rotulo: "O que paga", valor: c.descricao },
              { rotulo: "Valor devido", valor: c.valor_centavos != null ? formatarReais(c.valor_centavos) : "Sem valor definido", mono: c.valor_centavos != null },
              { rotulo: "Pago em, segundo o cliente", valor: formatarDia(c.pago_no_dia), mono: true },
              { rotulo: "Enviado em", valor: formatarDataHora(c.enviado_em), mono: true },
              ...(extras ?? []),
            ]}
          />
          {c.observacao && <p className="mtc-comprovante__obs"><span className="mtc-rotulo">Observação do cliente</span><br />{c.observacao}</p>}
          {decisao && <Aviso tipo={c.estado === "CONFERIDO" ? "sucesso" : "alerta"}>{decisao}.{c.estado === "RECUSADO" && c.motivo ? <> Motivo que o cliente leu: {c.motivo}</> : null}</Aviso>}
          {acoes && <div className="mtc-conferencia__acoes">{acoes}</div>}
          {links && <div className="mtc-linha" style={{ gap: "var(--e-3)", fontSize: "var(--t-sm)" }}>{links}</div>}
        </div>
      </div>
    </div>
  );
}
